import React from 'react'
import { motion } from 'framer-motion'
import { useParams } from 'react-router-dom'
import { Calendar, MapPin, Clock, Star, Users, Ticket, Share as ShareIcon, Navigation, MessageCircle } from 'lucide-react'
import EventActions from '../components/ui/EventActions'

const EventPage = () => {
  const { id } = useParams()
  const sample = {
    id,
    title: 'Jazz Night at The Wave',
    artist: 'Wichita Jazz Collective',
    venue: 'The Wave',
    startsAt: new Date().toISOString(),
    price: '$15',
    rating: 4.8,
    attendees: 120,
    address: '225 W Douglas Ave, Wichita, KS 67202',
    image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=1200',
    gallery: [
      'https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&cs=tinysrgb&w=800',
      'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=800',
      'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=800',
    ],
    lineup: ['Wichita Jazz Collective', 'Special Guest: Blue Note Trio'],
  }

  React.useEffect(() => {
    const data = {
      '@context': 'https://schema.org',
      '@type': 'MusicEvent',
      name: sample.title,
      startDate: sample.startsAt,
      location: {
        '@type': 'Place',
        name: sample.venue,
        address: sample.address,
      },
      performer: {
        '@type': 'MusicGroup',
        name: sample.artist,
      },
      offers: {
        '@type': 'Offer',
        price: sample.price.replace(/[^0-9.]/g, ''),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock'
      }
    }
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify(data)
    document.head.appendChild(script)
    return () => {
      document.head.removeChild(script)
    }
  }, [])

  const heroRef = React.useRef<HTMLDivElement>(null)
  const [showSticky, setShowSticky] = React.useState(false)
  React.useEffect(() => {
    const el = heroRef.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => {
      setShowSticky(!entry.isIntersecting)
    }, { threshold: 0.01 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const shareEvent = async () => {
    const shareData = {
      title: sample.title,
      text: `${sample.artist} at ${sample.venue}`,
      url: window.location.href,
    }
    try {
      if ((navigator as any).share) {
        await (navigator as any).share(shareData)
      } else {
        await navigator.clipboard.writeText(shareData.url)
        alert('Link copied!')
      }
    } catch (_) {}
  }

  return (
    <div className="pt-16 lg:pt-20 min-h-screen">
      {showSticky && (
        <div className="fixed bottom-4 left-0 right-0 z-40">
          <div className="container-max section-padding">
            <div className="glass-card p-4 flex items-center justify-between rounded-2xl">
              <div>
                <div className="text-white font-semibold">{sample.title}</div>
                <div className="text-white/70 text-sm">{sample.artist} • {sample.venue}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-2xl font-bold text-le-accent">{sample.price}</div>
                <button className="btn-primary">Get Tickets</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Hero */}
      <section className="section-padding pt-8 pb-6" ref={heroRef}>
        <div className="container-max">
          <div className="glass-card overflow-hidden">
            <div className="relative h-72 md:h-96">
              <img src={sample.image} alt={sample.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <h1 className="text-3xl md:text-5xl font-display font-bold text-white mb-2">{sample.title}</h1>
                  <div className="flex items-center gap-3 text-white/80">
                    <Star size={16} className="text-yellow-400 fill-current" />
                    <span>{sample.rating}</span>
                    <Users size={16} />
                    <span>{sample.attendees} going</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="btn-secondary flex items-center gap-2" onClick={shareEvent}><ShareIcon size={18} />Share</button>
                  <button className="btn-primary flex items-center gap-2"><Ticket size={18} />Get Tickets</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="section-padding pb-16">
        <div className="container-max grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-8">
          {/* Left: Info */}
          <motion.div className="space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="glass-card p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-white/80">
                <div className="flex items-center gap-2"><Calendar size={18} className="text-le-accent" /><span>{new Date(sample.startsAt).toLocaleString()}</span></div>
                <div className="flex items-center gap-2"><MapPin size={18} className="text-le-accent-2" /><span>{sample.venue}</span></div>
                <div className="flex items-center gap-2"><Clock size={18} className="text-le-amber" /><span>Doors 7:00 PM</span></div>
              </div>
              <div className="mt-4 text-white/70 leading-relaxed">
                Experience an unforgettable night of contemporary jazz with soulful improvisations and groove-heavy sets.
              </div>
              {sample.lineup?.length ? (
                <div className="mt-4">
                  <h3 className="text-white font-semibold mb-2">Lineup</h3>
                  <div className="flex flex-wrap gap-2">
                    {sample.lineup.map((a) => (
                      <span key={a} className="bg-white/10 text-white/80 text-xs px-3 py-1 rounded-full">{a}</span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="glass-card p-6">
              <h3 className="text-white font-semibold mb-4">Gallery</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {sample.gallery.map((g, i) => (
                  <div key={i} className="rounded-xl overflow-hidden">
                    <img src={g} alt={`gallery-${i}`} className="w-full h-40 object-cover" />
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-white font-semibold mb-4">Comments</h3>
              <div className="space-y-3">
                {[{a:'Alex',t:'See you there!'}, {a:'Jamie',t:'Venue sound is great for jazz.'}].map((c,i) => (
                  <div key={i} className="bg-white/5 rounded-xl p-4">
                    <div className="text-white/80 text-sm mb-1">{c.a}</div>
                    <div className="text-white/90">{c.t}</div>
                  </div>
                ))}
              </div>
              <form className="mt-4 flex gap-3" onSubmit={(e)=>{e.preventDefault();}}>
                <input className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-le-accent" placeholder="Write a comment..." />
                <button className="btn-primary px-6 flex items-center gap-2"><MessageCircle size={16}/>Post</button>
              </form>
            </div>
          </motion.div>

          {/* Right: Venue/Map */}
          <motion.aside className="space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}>
            <div className="glass-card p-6">
              <h3 className="text-white font-semibold mb-2">Venue</h3>
              <div className="text-white/80">{sample.venue}</div>
              <div className="text-white/60 text-sm">{sample.address}</div>
              <div className="h-48 rounded-xl mt-4 overflow-hidden">
                <iframe
                  title="map"
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(sample.address)}&output=embed`}
                />
              </div>
              <a className="btn-secondary mt-4 inline-flex items-center gap-2" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sample.address)}`} target="_blank" rel="noreferrer">
                <Navigation size={18} />
                Get Directions
              </a>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-white font-semibold mb-2">Price</h3>
              <div className="text-3xl font-bold text-le-accent">{sample.price}</div>
              <div className="mt-4">
                <a
                  className="btn-secondary inline-flex items-center gap-2"
                  href={(function(){
                    const start = new Date(sample.startsAt)
                    const end = new Date(start.getTime() + 2*60*60*1000)
                    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g,'').split('.')[0] + 'Z'
                    const dates = `${fmt(start)}/${fmt(end)}`
                    const text = encodeURIComponent(sample.title)
                    const location = encodeURIComponent(`${sample.venue}, ${sample.address}`)
                    const details = encodeURIComponent(`${sample.artist} — ${sample.title}`)
                    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${dates}&location=${location}&details=${details}`
                  })()}
                  target="_blank" rel="noreferrer"
                >
                  <Calendar size={18} /> Add to Google Calendar
                </a>
              </div>
              <div className="mt-4"><EventActions subject="event" subjectId={sample.id || 'unknown'} /></div>
            </div>
          </motion.aside>
        </div>
      </section>
    </div>
  )
}

export default EventPage